import React, { useState, useEffect, useCallback } from 'react';
import { useChromia } from '../context/ChromiaContext';
import { useGame } from '../context/GameContext';
import './CashOut.css';

const CASH_OUT_DURATION = 10; // 10 seconds
const ENTRY_FEE = 50; // 50 CHR entry fee
const DEFAULT_PLAYER_MASS = 10; // Starting mass for players

const CashOut = () => {
    const [isCashingOut, setIsCashingOut] = useState(false);
    const [countdown, setCountdown] = useState(CASH_OUT_DURATION);
    const [cashOutSuccess, setCashOutSuccess] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [cashOutResult, setCashOutResult] = useState(null); // Store the result including devMode
    
    const { requestCashOut, ftAccount, chrBalance } = useChromia();
    const { gameStart, player, socket } = useGame();

    // Check if user is a spectator (no cells or zero mass)
    const isSpectator = !player?.cells?.length || player?.massTotal === 0;

    // Calculate potential winnings based on mass
    // Formula: CHR = (currentMass / startingMass) * entryFee
    // This means eating a player with same mass doubles your CHR (adds 50 CHR)
    const calculateWinnings = useCallback(() => {
        const mass = player?.massTotal || 0;
        if (mass === 0) return 0;
        // CHR value = (mass / startingMass) * entryFee
        // e.g., mass 20 with starting 10 = (20/10) * 50 = 100 CHR
        const chrValue = (mass / DEFAULT_PLAYER_MASS) * ENTRY_FEE;
        return Math.floor(chrValue * 100) / 100; // Round to 2 decimal places
    }, [player?.massTotal]);

    // Handle countdown
    useEffect(() => {
        let interval;
        if (isCashingOut && countdown > 0) {
            interval = setInterval(() => {
                setCountdown(prev => prev - 1);
            }, 1000);
        } else if (isCashingOut && countdown === 0) {
            // Cash out successful - player survived!
            handleCashOutSuccess();
        }
        
        return () => clearInterval(interval);
    }, [isCashingOut, countdown]);

    // Listen for server events
    useEffect(() => {
        if (!socket) return;

        const handleDeath = () => {
            // Player died during cash out - cancel it
            setIsCashingOut(false);
            setCountdown(CASH_OUT_DURATION);
            setCashOutSuccess(false);
        };

        const handleCashOutFailed = (data) => {
            console.log('Cash out failed:', data.reason);
            setIsCashingOut(false);
            setCountdown(CASH_OUT_DURATION);
            setCashOutSuccess(false);
        };

        const handleCashOutServerSuccess = (data) => {
            console.log('Cash out success from server:', data);
            setCashOutSuccess(true);
            setIsCashingOut(false);
            setCashOutResult(data); // Store result including devMode flag
            
            // Log warning if in dev mode
            if (data.devMode) {
                console.warn('[CASH OUT] Running in DEV MODE - no actual CHR was transferred!');
                console.warn('[CASH OUT] Warning:', data.warning);
            }
            
            // Reload after delay (longer if dev mode to show warning)
            setTimeout(() => {
                window.location.reload();
            }, data.devMode ? 5000 : 3000);
        };

        const handleCashOutError = (data) => {
            console.error('Cash out error:', data.message);
            setIsCashingOut(false);
            setCountdown(CASH_OUT_DURATION);
            alert('Cash out error: ' + data.message);
        };

        socket.on('RIP', handleDeath);
        socket.on('cashOutFailed', handleCashOutFailed);
        socket.on('cashOutSuccess', handleCashOutServerSuccess);
        socket.on('cashOutError', handleCashOutError);
        
        return () => {
            socket.off('RIP', handleDeath);
            socket.off('cashOutFailed', handleCashOutFailed);
            socket.off('cashOutSuccess', handleCashOutServerSuccess);
            socket.off('cashOutError', handleCashOutError);
        };
    }, [socket]);

    const handleCashOutClick = () => {
        setShowConfirm(true);
    };

    // Helper to convert Uint8Array to hex string
    const toHexString = (arr) => {
        if (!arr) return '';
        if (arr instanceof Uint8Array) {
            return Array.from(arr).map(b => b.toString(16).padStart(2, '0')).join('').toUpperCase();
        }
        if (typeof arr === 'object' && arr.type === 'Buffer') {
            return Array.from(arr.data).map(b => b.toString(16).padStart(2, '0')).join('').toUpperCase();
        }
        return String(arr);
    };

    const handleConfirmCashOut = () => {
        setShowConfirm(false);
        setIsCashingOut(true);
        setCountdown(CASH_OUT_DURATION);
        
        // Convert account ID to hex string
        const accountIdHex = toHexString(ftAccount?.id);
        
        // Notify server that player is cashing out (frozen)
        if (socket) {
            socket.emit('cashOutStart', {
                accountId: accountIdHex,
                estimatedAmount: calculateWinnings()
            });
        }
    };

    const handleCancelCashOut = () => {
        setShowConfirm(false);
    };

    const handleAbortCashOut = () => {
        setIsCashingOut(false);
        setCountdown(CASH_OUT_DURATION);
        
        // Notify server that cash out was cancelled
        if (socket) {
            socket.emit('cashOutCancel');
        }
    };

    const handleCashOutSuccess = async () => {
        const winnings = calculateWinnings();
        
        try {
            // Request cash out from server
            const cashOutData = await requestCashOut(winnings);
            
            console.log('[CASH OUT] Sending cashOutComplete to server with:', cashOutData);
            
            // Notify server to process the cash out
            // The server will respond with 'cashOutSuccess' or 'cashOutError'
            if (socket) {
                socket.emit('cashOutComplete', cashOutData);
            }
            
            // Don't set success here - wait for server response via socket event
            // The 'cashOutSuccess' socket event handler will set setCashOutSuccess(true)
        } catch (err) {
            console.error('Cash out failed:', err);
            setIsCashingOut(false);
            setCountdown(CASH_OUT_DURATION);
            alert('Cash out request failed: ' + err.message);
        }
    };

    // Don't show if game hasn't started or user is a spectator
    if (!gameStart || isSpectator) {
        return null;
    }

    const winnings = calculateWinnings();

    return (
        <>
            {/* Cash Out Button */}
            {!isCashingOut && !cashOutSuccess && (
                <button 
                    className="cash-out-btn"
                    onClick={handleCashOutClick}
                >
                    💰 Cash Out
                </button>
            )}

            {/* Confirmation Modal */}
            {showConfirm && (
                <div className="cash-out-modal-overlay">
                    <div className="cash-out-modal">
                        <h3>💰 Cash Out?</h3>
                        <p className="cash-out-warning">
                            ⚠️ You will be <strong>frozen for {CASH_OUT_DURATION} seconds!</strong>
                        </p>
                        <p>If you survive, you'll receive:</p>
                        <div className="winnings-display">
                            <span className="winnings-amount">{winnings} CHR</span>
                        </div>
                        <p className="mass-info">Current mass: {player?.massTotal || 0}</p>
                        <div className="cash-out-actions">
                            <button className="confirm-btn" onClick={handleConfirmCashOut}>
                                Yes, Cash Out
                            </button>
                            <button className="cancel-btn" onClick={handleCancelCashOut}>
                                Keep Playing
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Countdown Overlay */}
            {isCashingOut && !cashOutSuccess && (
                <div className="cash-out-countdown-overlay">
                    <div className="countdown-container">
                        <h2>⏳ CASHING OUT...</h2>
                        <div className="countdown-circle">
                            <span className="countdown-number">{countdown}</span>
                        </div>
                        <p className="countdown-warning">
                            🚨 You are FROZEN! Survive {countdown} more seconds!
                        </p>
                        <p className="countdown-winnings">
                            Potential winnings: <strong>{winnings} CHR</strong>
                        </p>
                        <button className="abort-btn" onClick={handleAbortCashOut}>
                            ❌ Abort & Resume Playing
                        </button>
                    </div>
                </div>
            )}

            {/* Success Overlay */}
            {cashOutSuccess && (
                <div className="cash-out-success-overlay">
                    <div className="success-container">
                        <div className="success-icon">{cashOutResult?.devMode ? '⚠️' : '🎉'}</div>
                        <h2>{cashOutResult?.devMode ? 'Cash Out (Dev Mode)' : 'Cash Out Successful!'}</h2>
                        <p className="success-amount">
                            {cashOutResult?.devMode ? 'Simulated: ' : 'You earned '}
                            <strong>{cashOutResult?.amount || winnings} CHR</strong>
                        </p>
                        {cashOutResult?.devMode && (
                            <p className="dev-mode-warning" style={{ color: '#ff9800', fontSize: '14px', marginTop: '10px' }}>
                                ⚠️ Dev mode - No actual CHR transfer occurred.
                                <br />Contact administrator to enable real transfers.
                            </p>
                        )}
                        <p>Returning to menu...</p>
                    </div>
                </div>
            )}
        </>
    );
};

export default CashOut;

